import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  Send,
  Loader2,
  AlertTriangle,
  CheckCircle2,
  MessageSquare,
  Copy,
  Check,
  Clock,
  CalendarDays,
  AlertCircle,
  RefreshCw,
} from "lucide-react";

const SITUACAO_INFO = {
  vencida: {
    color: "bg-rose-100 text-rose-700 border-rose-200",
    label: "Vencida",
    icon: AlertCircle,
  },
  vence_hoje: {
    color: "bg-amber-100 text-amber-700 border-amber-200",
    label: "Vence hoje",
    icon: Clock,
  },
  a_vencer: {
    color: "bg-blue-100 text-blue-700 border-blue-200",
    label: "A vencer",
    icon: CalendarDays,
  },
};

function formatBRL(v) {
  return v
    .toFixed(2)
    .replace(".", ",")
    .replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default function CobrancaMassa({ onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [items, setItems] = useState([]);
  const [meta, setMeta] = useState({});
  const [selecionados, setSelecionados] = useState(new Set());
  const [enviadosIds, setEnviadosIds] = useState(new Set());
  const [copiedId, setCopiedId] = useState(null);
  const [filtroSituacao, setFiltroSituacao] = useState("todas");

  const generate = async (apenasVencidas = false) => {
    setGenerating(true);
    try {
      const r = await api.post(
        `/cobranca/gerar-links-massa?apenas_vencidas=${apenasVencidas}`
      );
      setItems(r.data.items || []);
      setMeta({
        total: r.data.total,
        mp_configurado: r.data.mp_configurado,
        links_gerados: r.data.links_gerados,
        erros: r.data.erros,
      });
      // Marca todos como selecionados por padrão
      setSelecionados(new Set((r.data.items || []).map((i) => i.aluno_id)));
      setEnviadosIds(new Set());
    } catch (e) {
      toast.error("Erro ao gerar links");
    } finally {
      setGenerating(false);
      setLoading(false);
    }
  };

  useEffect(() => {
    generate(false);
  }, []);

  const itemsFiltrados = items.filter((i) =>
    filtroSituacao === "todas" ? true : i.situacao === filtroSituacao
  );

  const toggleSelecao = (alunoId) => {
    setSelecionados((s) => {
      const novo = new Set(s);
      if (novo.has(alunoId)) novo.delete(alunoId);
      else novo.add(alunoId);
      return novo;
    });
  };

  const toggleTodos = () => {
    if (selecionados.size === itemsFiltrados.length) {
      setSelecionados(new Set());
    } else {
      setSelecionados(new Set(itemsFiltrados.map((i) => i.aluno_id)));
    }
  };

  const handleEnviarWA = (item) => {
    if (!item.whatsapp_url) {
      toast.error("Telefone do responsável não cadastrado");
      return;
    }
    window.open(item.whatsapp_url, "_blank");
    setEnviadosIds((s) => new Set(s).add(item.aluno_id));
  };

  const handleEnviarTodosSelecionados = async () => {
    const lista = items.filter((i) => selecionados.has(i.aluno_id));
    if (lista.length === 0) {
      toast.warning("Selecione pelo menos um aluno");
      return;
    }
    if (
      !window.confirm(
        `Vai abrir ${lista.length} abas do WhatsApp em sequência. Confirme cada envio. Continuar?`
      )
    ) {
      return;
    }
    let i = 0;
    const open = () => {
      if (i >= lista.length) {
        toast.success(`${lista.length} abas abertas`);
        return;
      }
      const item = lista[i];
      if (item.whatsapp_url) {
        window.open(item.whatsapp_url, "_blank");
        setEnviadosIds((s) => new Set(s).add(item.aluno_id));
      }
      i++;
      setTimeout(open, 700); // 700ms entre cada — evita popup blocker travar tudo
    };
    open();
  };

  const handleCopyLink = (item) => {
    if (!item.link_pagamento) {
      toast.error("Link de pagamento não disponível (Mercado Pago não configurado?)");
      return;
    }
    try {
      navigator.clipboard.writeText(item.link_pagamento);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = item.link_pagamento;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    setCopiedId(item.aluno_id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  if (loading) {
    return (
      <Layout onLogout={onLogout}>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-emerald-600" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout onLogout={onLogout}>
      <div className="max-w-5xl mx-auto space-y-6 pb-12">
        {/* Header */}
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            data-testid="btn-voltar-cobranca-massa"
            className="text-gray-600"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Voltar
          </Button>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate("/configuracoes/cobranca-automatica")}
              data-testid="btn-ir-config"
            >
              Configurar automação
            </Button>
            <Button
              variant="outline"
              onClick={() => generate(false)}
              disabled={generating}
              data-testid="btn-recarregar"
            >
              <RefreshCw
                className={`w-4 h-4 mr-2 ${generating ? "animate-spin" : ""}`}
              />
              Recarregar
            </Button>
          </div>
        </div>

        <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white">
                <Send className="w-6 h-6" />
              </div>
              <div className="flex-1">
                <h1
                  className="text-2xl font-bold mb-1"
                  style={{ fontFamily: "Space Grotesk" }}
                >
                  Disparar cobranças
                </h1>
                <p className="text-sm text-gray-600">
                  {meta.total} aluno(s) com mensalidade pendente.
                  {meta.mp_configurado
                    ? ` ${meta.links_gerados} link(s) MP gerado(s).`
                    : " Mercado Pago não configurado — apenas mensagem WhatsApp será enviada."}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {!meta.mp_configurado && (
          <Alert className="bg-amber-50 border-amber-200">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <AlertDescription className="text-sm text-amber-900">
              <strong>Mercado Pago ainda não configurado.</strong> Os links de
              pagamento online não serão gerados.{" "}
              <a
                href="/configuracoes/pagamento"
                className="underline font-semibold"
              >
                Configurar agora
              </a>
            </AlertDescription>
          </Alert>
        )}

        {/* Filtros */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm font-medium text-gray-700">Filtrar:</span>
              {[
                { id: "todas", label: "Todas", count: items.length },
                {
                  id: "vencida",
                  label: "Vencidas",
                  count: items.filter((i) => i.situacao === "vencida").length,
                },
                {
                  id: "vence_hoje",
                  label: "Vence hoje",
                  count: items.filter((i) => i.situacao === "vence_hoje").length,
                },
                {
                  id: "a_vencer",
                  label: "A vencer",
                  count: items.filter((i) => i.situacao === "a_vencer").length,
                },
              ].map((f) => (
                <button
                  key={f.id}
                  onClick={() => setFiltroSituacao(f.id)}
                  data-testid={`btn-filtro-${f.id}`}
                  className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                    filtroSituacao === f.id
                      ? "bg-emerald-600 text-white"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                  }`}
                >
                  {f.label} ({f.count})
                </button>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Toolbar */}
        {itemsFiltrados.length > 0 && (
          <Card>
            <CardContent className="pt-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <Checkbox
                  checked={
                    selecionados.size > 0 &&
                    selecionados.size === itemsFiltrados.length
                  }
                  onCheckedChange={toggleTodos}
                  data-testid="checkbox-todos"
                />
                Selecionar todos visíveis ({itemsFiltrados.length})
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-600">
                  {selecionados.size} selecionado(s)
                </span>
                <Button
                  onClick={handleEnviarTodosSelecionados}
                  disabled={selecionados.size === 0}
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="btn-enviar-todos"
                >
                  <Send className="w-4 h-4 mr-2" />
                  Enviar {selecionados.size} via WhatsApp
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Lista */}
        {itemsFiltrados.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center text-gray-500">
              <CheckCircle2 className="w-12 h-12 mx-auto text-emerald-300 mb-2" />
              <p className="text-sm">Nenhuma cobrança pendente nesta categoria.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-2">
            {itemsFiltrados.map((item) => {
              const sit = SITUACAO_INFO[item.situacao] || SITUACAO_INFO.vencida;
              const SitIcon = sit.icon;
              const isEnviado = enviadosIds.has(item.aluno_id);
              const isSelecionado = selecionados.has(item.aluno_id);
              return (
                <Card
                  key={item.aluno_id}
                  className={`transition-all ${
                    isSelecionado ? "border-emerald-400 bg-emerald-50/30" : ""
                  }`}
                  data-testid={`cobranca-item-${item.aluno_id}`}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <Checkbox
                        checked={isSelecionado}
                        onCheckedChange={() => toggleSelecao(item.aluno_id)}
                        data-testid={`checkbox-${item.aluno_id}`}
                        className="mt-1"
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-bold text-base">
                            {item.aluno_nome}
                          </span>
                          <Badge className={`border ${sit.color}`}>
                            <SitIcon className="w-3 h-3 mr-1" />
                            {sit.label}
                            {item.situacao === "vencida" &&
                              ` há ${item.dias} dias`}
                            {item.situacao === "a_vencer" &&
                              ` em ${item.dias} dias`}
                          </Badge>
                          {isEnviado && (
                            <Badge className="bg-emerald-600">
                              <Check className="w-3 h-3 mr-1" />
                              Enviado
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-gray-600 mt-1">
                          <strong>R$ {formatBRL(item.valor)}</strong>
                          {" • "}
                          Vencimento dia {item.dia_vencimento}
                          {" • "}
                          Resp: {item.responsavel_nome || "—"}
                          {item.responsavel_telefone &&
                            ` (${item.responsavel_telefone})`}
                        </div>
                      </div>
                      <div className="flex flex-col sm:flex-row gap-1.5 shrink-0">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleCopyLink(item)}
                          disabled={!item.link_pagamento}
                          data-testid={`btn-copiar-${item.aluno_id}`}
                          className="h-8"
                        >
                          {copiedId === item.aluno_id ? (
                            <Check className="w-3.5 h-3.5" />
                          ) : (
                            <Copy className="w-3.5 h-3.5" />
                          )}
                          <span className="ml-1 text-xs">Copiar link</span>
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => handleEnviarWA(item)}
                          disabled={!item.whatsapp_url}
                          className="bg-green-600 hover:bg-green-700 h-8"
                          data-testid={`btn-wa-${item.aluno_id}`}
                        >
                          <MessageSquare className="w-3.5 h-3.5" />
                          <span className="ml-1 text-xs">Enviar</span>
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </Layout>
  );
}
